import React from "react";
import { FiCopy, FiCheck } from "react-icons/fi";
import OnboardingCard from "../../components/OnboardingComponents/OnboardingCard";
import OnboardingButton from "../../components/OnboardingComponents/OnboardingButton";
import { getAvatarColor } from "./utils";

interface CustomerGridViewProps {
  customers: MasterCustomer[];
  copiedId: string | null;
  setCopiedId: (id: string | null) => void;
  copiedEmailId: string | null;
  setCopiedEmailId: (id: string | null) => void;
  impersonatingId: string | null;
  handleImpersonate: (customer: MasterCustomer) => void;
}

export const CustomerGridView = ({
  customers,
  copiedId,
  setCopiedId,
  copiedEmailId,
  setCopiedEmailId,
  impersonatingId,
  handleImpersonate,
}: CustomerGridViewProps) => (
  <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
    {customers.map((c: MasterCustomer) => {
      const fullName =
        [c.firstName, c.lastName].filter(Boolean).join(" ") || "Unknown User";

      const initials = (
        `${c.firstName?.[0] || ""}${c.lastName?.[0] || ""}` || "U"
      ).toUpperCase();

      return (
        <OnboardingCard
          key={c._id}
          className="p-[30px] flex flex-col h-full hover:-translate-y-1 transition-transform duration-300"
        >
          <div className="flex items-start justify-between gap-2 mb-6">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div
                className={`flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-full ${getAvatarColor(
                  c._id
                )} text-xl font-bold text-white shadow-sm`}
              >
                {initials}
              </div>

              <div className="min-w-0">
                <h3
                  className="font-['Canela_Text'] font-bold text-[20.28px] leading-[30.42px] text-[#1A1F3C] truncate"
                  title={fullName}
                >
                  {fullName}
                </h3>
                <div className="group/email flex items-center gap-1.5 min-w-0">
                  <p
                    className="font-['Proxima_Nova'] text-sm text-[#46464D] truncate"
                    title={c.email || "No Email"}
                  >
                    {c.email || "No Email"}
                  </p>
                  {c.email && (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(c.email);
                        setCopiedEmailId(c._id);
                        setTimeout(() => setCopiedEmailId(null), 2000);
                      }}
                      className="shrink-0 p-1 rounded-md text-[#A2B2C899] hover:text-[#1A1F3C] hover:bg-[#EAEBF0] opacity-0 group-hover/email:opacity-100 focus:opacity-100 transition-all"
                      title="Copy email"
                    >
                      {copiedEmailId === c._id ? (
                        <FiCheck size={14} className="text-green-600" />
                      ) : (
                        <FiCopy size={14} />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-8 rounded-lg border border-[#A2B2C833] bg-[#F8F9FA] p-4 relative">
            <p className="mb-1 font-['Proxima_Nova'] text-xs uppercase tracking-wide font-semibold text-[#A2B2C899]">
              Customer ID
            </p>
            <p
              className="font-['Proxima_Nova'] font-semibold text-[#030724] truncate pr-8"
              title={c.zohoCustomerId}
            >
              {c.zohoCustomerId || "Not Available"}
            </p>
            {c.zohoCustomerId && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(c.zohoCustomerId);
                  setCopiedId(c._id);
                  setTimeout(() => setCopiedId(null), 2000);
                }}
                className="absolute right-3 bottom-3.5 p-1.5 hover:bg-[#EAEBF0] rounded-md transition-colors text-[#A2B2C899] hover:text-[#1A1F3C]"
                title="Copy Customer ID"
              >
                {copiedId === c._id ? (
                  <FiCheck size={18} className="text-green-600" />
                ) : (
                  <FiCopy size={18} />
                )}
              </button>
            )}
          </div>

          <div className="mt-auto pt-2">
            <OnboardingButton
              title="IMPERSONATE"
              variant="filled"
              onClick={() => handleImpersonate(c)}
              loading={impersonatingId === c._id}
              disabled={impersonatingId === c._id}
            />
          </div>
        </OnboardingCard>
      );
    })}
  </div>
);
